/**
 * Panel lateral con detalles completos de objetivos
 * Muestra lista expandida por categoría con estado de cada objetivo
 */

import React, { useEffect } from 'react';
import { LessonObjective, UserLessonProgress, ObjectiveStatus } from '../../services/academicService';
import './ObjectivesDetailPanel.css';

interface ObjectivesDetailPanelProps {
  objectives: LessonObjective[];
  progress: UserLessonProgress | null;
  onClose: () => void;
  isOpen: boolean;
}

type ObjectiveType = 'gramatica' | 'vocabulario' | 'comunicativo';

interface CategorizedObjective {
  objective: LessonObjective;
  status: ObjectiveStatus | null;
  type: ObjectiveType;
}

const CATEGORY_CONFIG = {
  gramatica: { icon: '📝', label: 'GRAMÁTICA', color: '#1976d2' },
  vocabulario: { icon: '📚', label: 'VOCABULARIO', color: '#7b1fa2' },
  comunicativo: { icon: '💬', label: 'COMUNICATIVO', color: '#388e3c' },
};

export const ObjectivesDetailPanel: React.FC<ObjectivesDetailPanelProps> = ({
  objectives,
  progress,
  onClose,
  isOpen,
}) => {
  // Cerrar con ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevenir scroll del body cuando el panel está abierto
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Categorizar objetivos
  const categorizeObjectives = (): Record<ObjectiveType, CategorizedObjective[]> => {
    const categorized: Record<ObjectiveType, CategorizedObjective[]> = {
      gramatica: [],
      vocabulario: [],
      comunicativo: [],
    };

    objectives.forEach((objective) => {
      const type = (objective as any).type as ObjectiveType | undefined;
      const status = progress?.objectives_status?.[objective.description] || null;

      if (type && categorized[type]) {
        categorized[type].push({ objective, status, type });
      }
    });

    return categorized;
  };

  const categorizedObjectives = categorizeObjectives();

  // Calcular estadísticas por categoría
  const getCategoryStats = (type: ObjectiveType) => {
    const objectives = categorizedObjectives[type];
    const completed = objectives.filter((o) => o.status?.completed).length;
    const total = objectives.length;
    return { completed, total, percentage: total > 0 ? (completed / total) * 100 : 0 };
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay para cerrar al hacer clic fuera */}
      <div className="objectives-panel-overlay" onClick={onClose} />

      {/* Panel lateral */}
      <div className={`objectives-detail-panel ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="panel-header">
          <h2 className="panel-title">
            <span className="panel-title-icon">🎯</span>
            Objetivos de la Lección
          </h2>
          <button
            className="panel-close-button"
            onClick={onClose}
            type="button"
            aria-label="Cerrar panel"
          >
            ✕
          </button>
        </div>

        {/* Contenido scrolleable */}
        <div className="panel-content">
          {(Object.keys(categorizedObjectives) as ObjectiveType[]).map((type) => {
            const objectives = categorizedObjectives[type];
            if (objectives.length === 0) return null;

            const config = CATEGORY_CONFIG[type];
            const stats = getCategoryStats(type);

            return (
              <div key={type} className="category-section">
                {/* Header de categoría */}
                <div className="category-header" style={{ borderLeftColor: config.color }}>
                  <div className="category-title">
                    <span className="category-icon" aria-hidden="true">
                      {config.icon}
                    </span>
                    <span className="category-label">{config.label}</span>
                    <span className="category-count">
                      ({stats.completed}/{stats.total})
                    </span>
                  </div>
                  <div className="category-progress-bar">
                    <div
                      className="category-progress-fill"
                      style={{
                        width: `${stats.percentage}%`,
                        backgroundColor: config.color,
                      }}
                    />
                  </div>
                </div>

                {/* Lista de objetivos */}
                <div className="objectives-list">
                  {objectives.map(({ objective, status }) => {
                    const isCompleted = status?.completed || false;
                    const correctCount = status?.correct_count || 0;
                    const hasProgress = correctCount > 0;

                    return (
                      <div
                        key={objective.id}
                        className={`objective-item ${
                          isCompleted ? 'completed' : hasProgress ? 'in-progress' : 'pending'
                        }`}
                      >
                        <div className="objective-status-icon">
                          {isCompleted ? (
                            <span className="status-icon completed" aria-label="Completado">
                              ✓
                            </span>
                          ) : hasProgress ? (
                            <span className="status-icon in-progress" aria-label="En progreso">
                              ●
                            </span>
                          ) : (
                            <span className="status-icon pending" aria-label="Pendiente">
                              ○
                            </span>
                          )}
                        </div>
                        <div className="objective-content">
                          <div className="objective-description">{objective.description}</div>
                          {hasProgress && !isCompleted && (
                            <div className="objective-progress-hint">
                              En progreso ({correctCount} {correctCount === 1 ? 'vez' : 'veces'})
                            </div>
                          )}
                          {isCompleted && status?.completed_at && (
                            <div className="objective-completed-at">
                              Completado{' '}
                              {new Date(status.completed_at).toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Mensaje si no hay objetivos */}
          {objectives.length === 0 && (
            <div className="no-objectives-message">
              <p>No hay objetivos definidos para esta lección.</p>
            </div>
          )}
        </div>

        {/* Footer con resumen */}
        {objectives.length > 0 && (
          <div className="panel-footer">
            <div className="footer-summary">
              <span className="footer-icon">🎉</span>
              <span className="footer-text">
                {progress
                  ? `${
                      Object.values(progress.objectives_status).filter((s) => s?.completed).length
                    }/${objectives.length} completados`
                  : `0/${objectives.length} completados`}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
